import React from 'react';
import config from '../config';

const Home = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Professional */}
      <section className="relative bg-white pt-24 md:pt-32 pb-12 md:pb-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 md:gap-3 bg-sky-50 border border-sky-200 px-4 md:px-6 py-2 md:py-3 rounded-full mb-6 md:mb-8 text-sm md:text-base">
              <svg className="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="width"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              <span className="text-sky-900 font-semibold">RoadSafetyBC Approved Clinic</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 md:mb-6 leading-tight px-2">
              Driver Medical
              <br />
              Examinations in BC
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-6 md:mb-8 max-w-3xl mx-auto px-4">
              Professional healthcare assessments for driver medical fitness by qualified providers at South Vancouver Medical Clinic
            </p>

            {/* Key Benefits */}
            <div className="flex flex-wrap justify-center gap-6 mb-10">
              <div className="flex items-center gap-2 text-gray-700">
                <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Same-Day Appointments</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Professional Team</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Fast Results</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
              <a
                href={config.southVanLogin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white text-base md:text-lg font-semibold px-8 md:px-10 py-4 md:py-4 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="width" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Book Online - $150
              </a>
              <div className="text-gray-600 text-center">
                <span className="block text-sm">Walk-in available</span>
                <span className="font-semibold text-lg">$250</span>
              </div>
            </div>

            {/* Trust Indicator */}
            <p className="mt-6 text-sm text-gray-500">✓ 500+ Examinations Completed • Serving BC Drivers Since 2019</p>
          </div>
        </div>
      </section>

      {/* Who Needs This - Clean Cards */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">Who Needs a Driver Medical Exam?</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">You may be required to complete a Driver's Medical Examination Report if you meet any of the following criteria:</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="width" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: 'Age 80+',
                desc: 'Drivers 80 years or older require regular assessments'
              },
              {
                icon: (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="width"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                ),
                title: 'Commercial Drivers',
                desc: "Hold a commercial driver's licence"
              },
              {
                icon: (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="width"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                ),
                title: 'Medical Condition',
                desc: 'Known medical condition (e.g., diabetes, heart condition)'
              },
              {
                icon: (
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="width"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                ),
                title: 'RoadSafetyBC Notice',
                desc: 'Received notification from RoadSafetyBC'
              }
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 border border-gray-200 p-6 rounded-xl hover:border-sky-300 hover:shadow-lg transition-all duration-200">
                <div className="text-sky-600 mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Step by Step */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">Simple 3-Step Process</h2>
          <p className="text-center text-gray-600 mb-12">Getting your driver medical examination is quick and straightforward</p>

          <div className="space-y-8">
            {[
              {
                step: '1',
                title: 'Book Your Appointment',
                desc: 'Book online for our preferred rate of $150. Online booking is fast and convenient, or walk-ins are welcome at $250.',
                highlight: 'Save $100 by booking online'
              },
              {
                step: '2',
                title: 'Get Your ICBC Form',
                desc: "Visit any ICBC driver licensing office to obtain your Driver's Medical Examination Report form. This paper form is mandatory for the assessment.",
                highlight: 'Must bring paper form from ICBC'
              },
              {
                step: '3',
                title: 'Attend Your Exam',
                desc: 'Visit our clinic with your ICBC form and corrective lenses (glasses or contacts). The examination takes approximately 30 minutes.',
                highlight: 'Completed by qualified healthcare provider'
              }
            ].map((item, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-4 sm:gap-6 bg-white border border-gray-200 p-6 md:p-8 rounded-xl">
                <div className="flex-shrink-0 mx-auto sm:mx-0">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-sky-600 text-white text-2xl font-bold rounded-full flex items-center justify-center shadow-lg">{item.step}</div>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">{item.title}</h3>
                  <p className="text-sm md:text-base text-gray-600 mb-3 leading-relaxed">{item.desc}</p>
                  <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-lg text-sm text-emerald-800">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {item.highlight}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* What to Bring */}
          <div className="mt-12 bg-amber-50 border-2 border-amber-200 p-8 rounded-xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="width"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              What to Bring to Your Appointment
            </h3>
            <ul className="grid md:grid-cols-2 gap-3">
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700">Corrective lenses (glasses or contact lenses) if you wear them</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700">ICBC Driver's Medical Examination Report (paper form)</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700">Valid photo identification</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700">Allow 30 minutes for the complete examination</span>
              </li>
            </ul>
            <div className="mt-4 pt-4 border-t border-amber-200">
              <p className="text-amber-900 font-semibold flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="width"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                Important: Without the ICBC form, the examination cannot be completed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About / Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 bg-sky-50 px-4 py-2 rounded-full text-sm text-sky-900 font-semibold mb-4">🏥 Trusted Healthcare Partner</div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Supporting Road Safety in British Columbia</h2>
          </div>

          <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
            <p>
              To support road safety in British Columbia, the <strong className="text-gray-900">South Vancouver Medical Clinic</strong> assists RoadSafetyBC in assessing the medical fitness of
              drivers.
            </p>
            <p>
              This is done through the Driver's Medical Examination Report, available at all ICBC driver licensing offices. The report provides important information to determine whether a medical
              condition may impact an individual's ability to drive safely. Each report is completed by a qualified health care provider.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-sky-50 to-cyan-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 md:p-12 text-center">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-8 md:mb-12">Ready to Book Your Examination?</h2>

              <div className="grid sm:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
                {/* Online Booking */}
                <div className="relative bg-gradient-to-br from-sky-500 to-cyan-500 text-white p-6 md:p-8 rounded-xl shadow-lg">
                  <div className="absolute top-3 right-3 md:top-4 md:right-4 bg-emerald-400 text-emerald-900 text-xs font-bold px-2 md:px-3 py-1 rounded-full">SAVE $100</div>
                  <div className="text-sm font-semibold mb-2 opacity-90">Online Booking</div>
                  <div className="text-5xl md:text-6xl font-bold mb-4">$150</div>
                  <ul className="text-left space-y-2 mb-6 text-sm opacity-90">
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Preferred rate
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Convenient scheduling
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Same-day available
                    </li>
                  </ul>
                  <a
                    href={config.southVanLogin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block w-full bg-white text-sky-600 font-bold py-3 md:py-4 px-6 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                  >
                    Book Online Now
                  </a>
                </div>

                {/* Walk-in */}
                <div className="bg-gray-100 border-2 border-gray-300 p-6 md:p-8 rounded-xl">
                  <div className="text-sm font-semibold text-gray-600 mb-2">Walk-in</div>
                  <div className="text-5xl md:text-6xl font-bold text-gray-700 mb-4">$250</div>
                  <ul className="text-left space-y-2 mb-6 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Standard rate
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      No appointment needed
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Subject to availability
                    </li>
                  </ul>
                  <div className="text-gray-500 font-semibold py-3 px-6 rounded-lg border-2 border-gray-300">Visit During Hours</div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="width"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  RoadSafetyBC Approved
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="width"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  500+ Exams Completed
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="width" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Fast 30-Minute Exams
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export { Home };
